import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { registerWithFirebase } from '../services/authService';

interface Industry {
  id: number;
  name: string;
}

interface ERPSystemType {
  id: number;
  name: string;
  vendor: string;
}

interface CompanyAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington D.C.' }
];

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    main_contact_name: '',
    main_contact_email: '',
    main_contact_phone: '',
    password: '',
    confirm_password: '',
    industry_id: '',
    erp_system_type_id: '',
    terms_accepted: false,
  });
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [erpSystems, setErpSystems] = useState<ERPSystemType[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch reference data on mount
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [industriesRes, erpSystemsRes] = await Promise.all([
          api.get('/reference-data/industries'),
          api.get('/reference-data/erp-system-types'),
        ]);
        setIndustries(industriesRes.data);
        setErpSystems(erpSystemsRes.data);
      } catch (err) {
        console.error('Failed to load reference data:', err);
      }
    };
    fetchReferenceData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate terms acceptance
    if (!formData.terms_accepted) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }

    // Validate phone number
    if (!formData.main_contact_phone) {
      setError('Phone number is required');
      return;
    }

    // Validate password
    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Validate password confirmation
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Create Firebase user account
      const userCredential = await registerWithFirebase(
        formData.main_contact_email,
        formData.password
      );

      // Step 2: Get Firebase UID
      const firebaseUid = userCredential.user.uid;

      // Step 3: Register company with backend, including Firebase UID
      // Using strongly-typed address object per coding standards
      const address: CompanyAddress = {
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
      };

      const registrationData = {
        company_name: formData.company_name,
        address: address,  // Strongly-typed address object
        main_contact_name: formData.main_contact_name,
        main_contact_email: formData.main_contact_email,
        main_contact_phone: formData.main_contact_phone,
        firebase_uid: firebaseUid,
        industry_id: formData.industry_id ? parseInt(formData.industry_id) : null,
        erp_system_type_id: formData.erp_system_type_id ? parseInt(formData.erp_system_type_id) : null,
        terms_accepted: formData.terms_accepted,
      };

      await api.post('/auth/register-company', registrationData);
      // Registration successful! Redirect to email verification page
      navigate('/verify-email-pending');
    } catch (err: any) {
      // If backend registration fails, we should ideally delete the Firebase user
      // But for now, just show the error
      setError(err.response?.data?.detail || err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
         style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold"
              style={{ color: 'var(--text-primary)' }}>
            Register Your Company
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
            Get started with Vellrix Manufacturing Intelligence
          </p>
        </div>

        <div className="rounded-lg shadow-md p-8" style={{ background: 'var(--bg-secondary)' }}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Company Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b pb-2" style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                Company Information
              </h3>

              <div>
                <label htmlFor="company_name" className="block text-sm font-medium mb-1"
                       style={{ color: 'var(--text-primary)' }}>
                  Company Name *
                </label>
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                />
              </div>

              {/* Company Address */}
              <div>
                <label htmlFor="street" className="block text-sm font-medium mb-1"
                       style={{ color: 'var(--text-primary)' }}>
                  Street Address *
                </label>
                <input
                  id="street"
                  name="street"
                  type="text"
                  required
                  value={formData.street}
                  onChange={handleChange}
                  placeholder="123 Main St"
                  className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium mb-1"
                         style={{ color: 'var(--text-primary)' }}>
                    City *
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium mb-1"
                         style={{ color: 'var(--text-primary)' }}>
                    State *
                  </label>
                  <select
                    id="state"
                    name="state"
                    required
                    value={formData.state}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="zip" className="block text-sm font-medium mb-1"
                         style={{ color: 'var(--text-primary)' }}>
                    ZIP Code *
                  </label>
                  <input
                    id="zip"
                    name="zip"
                    type="text"
                    required
                    value={formData.zip}
                    onChange={handleChange}
                    placeholder="12345"
                    maxLength={10}
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="industry_id" className="block text-sm font-medium mb-1"
                       style={{ color: 'var(--text-primary)' }}>
                  Industry
                </label>
                <select
                  id="industry_id"
                  name="industry_id"
                  value={formData.industry_id}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  <option value="">Select Industry</option>
                  {industries.map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="erp_system_type_id" className="block text-sm font-medium mb-1"
                       style={{ color: 'var(--text-primary)' }}>
                  ERP System
                </label>
                <select
                  id="erp_system_type_id"
                  name="erp_system_type_id"
                  value={formData.erp_system_type_id}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  <option value="">Select ERP System</option>
                  {erpSystems.map((erp) => (
                    <option key={erp.id} value={erp.id}>
                      {erp.name} ({erp.vendor})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Main Contact */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b pb-2" style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                Main Contact (Will become first administrator)
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="main_contact_name" className="block text-sm font-medium mb-1"
                         style={{ color: 'var(--text-primary)' }}>
                    Full Name *
                  </label>
                  <input
                    id="main_contact_name"
                    name="main_contact_name"
                    type="text"
                    required
                    value={formData.main_contact_name}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="main_contact_phone" className="block text-sm font-medium mb-1"
                         style={{ color: 'var(--text-primary)' }}>
                    Phone Number *
                  </label>
                  <input
                    id="main_contact_phone"
                    name="main_contact_phone"
                    type="tel"
                    required
                    value={formData.main_contact_phone}
                    onChange={handleChange}
                    placeholder="+1-555-123-4567"
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="main_contact_email" className="block text-sm font-medium mb-1"
                       style={{ color: 'var(--text-primary)' }}>
                  Email Address *
                </label>
                <input
                  id="main_contact_email"
                  name="main_contact_email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.main_contact_email}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-1"
                         style={{ color: 'var(--text-primary)' }}>
                    Password *
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                  <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Minimum 8 characters
                  </p>
                </div>

                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium mb-1"
                         style={{ color: 'var(--text-primary)' }}>
                    Confirm Password *
                  </label>
                  <input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.confirm_password}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms_accepted"
                    name="terms_accepted"
                    type="checkbox"
                    checked={formData.terms_accepted}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms_accepted" style={{ color: 'var(--text-primary)' }}>
                    I accept the{' '}
                    <a
                      href="/api/v1/legal/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-500 underline"
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a
                      href="/api/v1/legal/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-500 underline"
                    >
                      Privacy Policy
                    </a>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Account...' : 'Create Company Account'}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;